import { useSetAtom } from 'jotai';
import { useCallback } from 'react';

import {
  authDialogContextAtom,
  downloadStageContextAtom,
} from '@/components/DialogStage/atom';
import { useStage } from '@/components/DialogStage/useStage';
import { conversionPoints } from '@/components/Modals/context/ModalRoutingContext';
import { ModalConversionPoints } from '@/components/Modals/shared-types';
import { useReturningUser } from '@/hooks/analytics/useReturningUser';
import { recordConversion } from '@/utils/analytics/conversion';

/**
 * Triggers that legitimately need to attribute an auth-flow open back to the
 * feature that prompted it (e.g. "user tried to add to a board → ended up
 * subscribing"). Each value drives up to three things:
 *
 * - {@link CONTEXT_TO_CONVERSION_POINT} — the conversion-source string that
 *   lands in `conversion_source` localStorage and is attached to the
 *   `purchase` analytics event (it is not sent to the backend).
 * - {@link SIGNUP_TITLE_BY_CONTEXT} — the heading the SignupOptionsScreen
 *   renders, mirroring what the OLD SignUp modal showed for the matching
 *   `welcomeMessageType`. Add-to-board falls back to the screen's default
 *   because the OLD modal showed no title in that case.
 * - {@link SIGNUP_DESCRIPTION_BY_CONTEXT} — an optional override for the
 *   SignupOptionsScreen description; omitted contexts keep the default line.
 *
 * The scene routing (Login vs Signup-options) does not branch on context;
 * it's driven entirely by isReturningUser. The Login screen never had
 * contextual copy in the OLD modal and keeps that behaviour here.
 */
export type AuthDialogContext =
  | 'add-to-board'
  | 'playlist-generator'
  | 'pricing-plan'
  | 'pricing-free-plan'
  | 'partner-application';

const CONTEXT_TO_CONVERSION_POINT: Record<
  AuthDialogContext,
  ModalConversionPoints
> = {
  'add-to-board': ModalConversionPoints.AuthAddToBoard,
  'playlist-generator': ModalConversionPoints.AuthPlaylistGenerator,
  'pricing-plan': ModalConversionPoints.PricingPage,
  'pricing-free-plan': ModalConversionPoints.PricingPage,
  'partner-application': ModalConversionPoints.AuthPartnerApplication,
};

/**
 * Signup-screen titles per trigger. Copy confirmed by product — keep in
 * sync with the doc before editing. Triggers omitted here fall back to the
 * screen's default heading.
 */
export const SIGNUP_TITLE_BY_CONTEXT: Partial<
  Record<AuthDialogContext, string>
> = {
  'add-to-board': 'Create an account to build your board',
  'playlist-generator': 'Create an account to generate a playlist',
  'partner-application': 'Create an account to become a partner',
};

export const SIGNUP_DESCRIPTION_BY_CONTEXT: Partial<
  Record<AuthDialogContext, string>
> = {
  'partner-application':
    'You need an Uppbeat account to join the Creator Partner program.',
};

/**
 * Open the new AuthDialog (DialogStage) with returning-user routing and
 * optional conversion-source attribution + contextual title. Mirrors
 * {@link useSignInOrSignUp} but targets the new dialog system; call sites
 * that previously dispatched via
 * `useAccountModals().openModalWithConversionPoint(...)` should land here.
 * The legacy `AccountModals` path is untouched for non-migrated triggers.
 *
 * Callers are expected to guard on `useIsLoggedIn()` themselves — the hook
 * does not no-op for logged-in users.
 */
export const useOpenAuthDialog = () => {
  const { open } = useStage();
  const setContext = useSetAtom(authDialogContextAtom);
  const setDownloadContext = useSetAtom(downloadStageContextAtom);
  const {
    data: { isReturningUser },
  } = useReturningUser();

  return useCallback(
    (opts?: { context?: AuthDialogContext }) => {
      // Setter ordering: write the context first so the scene reads the
      // right value on its first render. React batches both atom writes in
      // an event handler, so the stage renders once with both applied.
      setContext(opts?.context ?? null);
      // Closing the stage keeps the last download context, and the shared
      // sign-up scenes route on it, so a plain auth open must drop it.
      setDownloadContext(null);
      open(isReturningUser ? 'auth.login' : 'auth.signup-options');

      if (opts?.context) {
        const conversionPoint = CONTEXT_TO_CONVERSION_POINT[opts.context];
        recordConversion({ source: conversionPoints[conversionPoint] });
      }
    },
    [open, setContext, setDownloadContext, isReturningUser]
  );
};
